/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dispatch, SetStateAction } from 'react';
import { RefetchOptions, QueryObserverResult, UseMutationResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { t } from 'i18next';
import { enqueueSnackbar } from 'notistack';

import { CreateEntityResponse, Language } from '../types';
import i18n from '@/i18n';

import { getContentLanguage } from './getContentLanguage.ts';

export const numberSortComparator = (v1: number, v2: number) => {
  return v1 - v2;
};

export const languageSortComparator = (v1: Language, v2: Language) => {
  const currentLang = getContentLanguage(i18n.language);
  return String(v1[currentLang] || '').localeCompare(String(v2[currentLang] || ''), undefined, {
    sensitivity: 'accent',
    ignorePunctuation: true,
    numeric: true,
  });
};

const clearSelectedRows = (setSelectedRows: Dispatch<SetStateAction<number[]>>) => {
  setSelectedRows([]);
};

export const onSelectRow = (
  selectedRowId: number,
  setSelectedRows: Dispatch<SetStateAction<number[]>>,
) => {
  setSelectedRows((prevState) => {
    const isSelected = prevState.includes(selectedRowId);

    if (isSelected) {
      return prevState.filter((row) => row !== selectedRowId);
    }

    return [selectedRowId, ...prevState];
  });
};

export const findRowById = <ItemType extends { id: number }>(
  items: ItemType[],
  selectedRowId: number,
): ItemType | undefined => items.find((item) => item.id === selectedRowId);

export const onSelectAll = <ItemType extends { id: number }>(
  data: { results: ItemType[]; total_results: number } | undefined,
  selectedRows: number[],
  setSelectedRows: Dispatch<SetStateAction<number[]>>,
) => {
  if (!data) return;
  if (data.results.length === selectedRows.length) {
    return setSelectedRows([]);
  }
  const rowIds = data.results.map((row) => row.id);
  setSelectedRows(rowIds);
};

const mutateForEachRow = async ({
  selectedRows,
  mutation,
  errorTranslationJsonKey,
}: {
  selectedRows: number[];
  mutation: UseMutationResult<AxiosResponse<CreateEntityResponse, any>, unknown, number, unknown>;
  errorTranslationJsonKey: string;
}) => {
  for (const rowId of selectedRows) {
    const { status, data } = await mutation.mutateAsync(rowId, {
      onError: () => {
        enqueueSnackbar(t(errorTranslationJsonKey, { rowId }), { variant: 'error' });
      },
    });
    if (status !== 200 || !data) {
      enqueueSnackbar(t(errorTranslationJsonKey, { rowId }), { variant: 'error' });
    }
  }
};

const handleOperation = async ({
  mutateCallback,
  clearSelectedRows,
  setSelectedRows,
  refetch,
}: {
  mutateCallback: () => Promise<void>;
  clearSelectedRows: (setSelectedRows: Dispatch<SetStateAction<number[]>>) => void;
  setSelectedRows: Dispatch<SetStateAction<number[]>>;
  refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<any, Error>>;
}) => {
  await mutateCallback();
  clearSelectedRows(setSelectedRows);
  await refetch();
};

export const handleDuplicateItems = async ({
  selectedRows,
  setSelectedRows,
  refetch,
  duplicateMutation,
}: {
  selectedRows: number[];
  setSelectedRows: Dispatch<SetStateAction<number[]>>;
  refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<any, Error>>;
  duplicateMutation: UseMutationResult<
    AxiosResponse<CreateEntityResponse, any>,
    unknown,
    any,
    unknown
  >;
}) => {
  await handleOperation({
    mutateCallback: () =>
      mutateForEachRow({
        selectedRows,
        mutation: duplicateMutation,
        errorTranslationJsonKey: 'ERRORS.TABLE_ROW_DUPLICATE_FAILURE',
      }),
    clearSelectedRows,
    setSelectedRows,
    refetch,
  });
};

export const handleDeleteItems = async ({
  selectedRows,
  setSelectedRows,
  refetch,
  deleteMutation,
}: {
  selectedRows: number[];
  setSelectedRows: Dispatch<SetStateAction<number[]>>;
  refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<any, Error>>;
  deleteMutation: UseMutationResult<
    AxiosResponse<CreateEntityResponse, any>,
    unknown,
    number,
    unknown
  >;
}) => {
  await handleOperation({
    mutateCallback: () =>
      mutateForEachRow({
        selectedRows,
        mutation: deleteMutation,
        errorTranslationJsonKey: 'ERRORS.TABLE_ROW_DELETE_FAILURE',
      }),
    clearSelectedRows,
    setSelectedRows,
    refetch,
  });
};

export const handleDeactivateItems = async ({
  selectedRows,
  setSelectedRows,
  refetch,
  deactivateMutation,
}: {
  selectedRows: number[];
  setSelectedRows: Dispatch<SetStateAction<number[]>>;
  refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<any, Error>>;
  deactivateMutation: UseMutationResult<
    AxiosResponse<CreateEntityResponse, any>,
    unknown,
    number,
    unknown
  >;
}) => {
  await handleOperation({
    mutateCallback: () =>
      mutateForEachRow({
        selectedRows,
        mutation: deactivateMutation,
        errorTranslationJsonKey: 'ERRORS.TABLE_ROW_DEACTIVATE_FAILURE',
      }),
    clearSelectedRows,
    setSelectedRows,
    refetch,
  });
};

export const handleActivateItems = async ({
  selectedRows,
  setSelectedRows,
  refetch,
  activateMutation,
}: {
  selectedRows: number[];
  setSelectedRows: Dispatch<SetStateAction<number[]>>;
  refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<any, Error>>;
  activateMutation: UseMutationResult<
    AxiosResponse<CreateEntityResponse, any>,
    unknown,
    number,
    unknown
  >;
}) => {
  await handleOperation({
    mutateCallback: () =>
      mutateForEachRow({
        selectedRows,
        mutation: activateMutation,
        errorTranslationJsonKey: 'ERRORS.TABLE_ROW_ACTIVATE_FAILURE',
      }),
    clearSelectedRows,
    setSelectedRows,
    refetch,
  });
};
